import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { acquirerService } from '@services/acquirerService';
import { Acquirer, AcquirerFormData } from '@/types/acquirer';

export const AcquirerTab: React.FC = () => {
  const [items, setItems] = useState<Acquirer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<Acquirer | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AcquirerFormData>({ id: 0, name: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await acquirerService.getAcquirers({ page: currentPage, pageSize });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, [currentPage]);

  const openCreateForm = () => {
    setEditingItem(null);
    setFormData({ id: 0, name: '' });
    setFormErrors({});
    setShowForm(true);
  };

  const openEditForm = (item: Acquirer) => {
    setEditingItem(item);
    setFormData({ id: item.id, name: item.name });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.id && !editingItem) errors.id = 'ID é obrigatório';
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    if (formData.name.length > 16) errors.name = 'Nome deve ter no máximo 16 caracteres';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      if (editingItem) {
        await acquirerService.updateAcquirer(editingItem.id, formData);
      } else {
        await acquirerService.createAcquirer(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await acquirerService.deleteAcquirer(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Gerencie os adquirentes cadastrados no sistema.</p>
        <PermissionGuard permission="receiving_hub.write">
          <Button size="sm" onClick={openCreateForm} icon={<Plus size={16} />}>Novo Adquirente</Button>
        </PermissionGuard>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              {editingItem ? 'Editar Adquirente' : 'Novo Adquirente'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {!editingItem && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">ID <span className="text-red-500">*</span></label>
                  <Input type="number" value={formData.id || ''} onChange={(e) => setFormData(prev => ({ ...prev, id: parseInt(e.target.value) || 0 }))} placeholder="ID do adquirente" />
                  {formErrors.id && <p className="text-xs text-red-500 mt-1">{formErrors.id}</p>}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nome <span className="text-red-500">*</span></label>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Nome do adquirente" maxLength={16} />
                {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                <p className="text-xs text-slate-500 mt-1">{formData.name.length}/16 caracteres</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)} icon={<X size={14} />}>Cancelar</Button>
              <Button size="sm" onClick={handleSave} loading={saving} icon={<Save size={14} />}>
                {editingItem ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Nome</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-12 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-32 animate-pulse" /></TableCell>
                    <TableCell><div className="h-4 bg-slate-200 rounded w-20 animate-pulse ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-slate-500 py-8">Nenhum adquirente encontrado</TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">{item.id}</TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="receiving_hub.write">
                          <Button variant="secondary" size="sm" onClick={() => openEditForm(item)} icon={<Edit size={14} />}>Editar</Button>
                        </PermissionGuard>
                        <PermissionGuard permission="receiving_hub.write">
                          {deleteConfirm === item.id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>Confirmar</Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(item.id)} icon={<Trash2 size={14} />}>Excluir</Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
            <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      )}
    </div>
  );
};
